import { useState } from 'react';
import { useApp, useSession, useUi } from '../app/context';
import { openDocumentCard } from '../app/commands';
import { notify, openAccount, patchUi, type ContextMenuRequest } from '../app/ui';
import { MAX_WINDOWS } from '../core/geometry';
import { windowOf } from '../core/session';
import { toggleInline } from '../editor/markdownCommands';
import { outdentLines, indentLines } from '../editor/indent';
import { runSpec } from '../editor/setup';
import { isMac, shortcutHint, shortcutLabel } from './keys';
import { BoldIcon, CloudIcon, CodeIcon, CurrentLineIcon, GridIcon, IndentIcon, ItalicIcon, LineNumbersIcon, LinkIcon, MoonIcon, OutdentIcon, SearchIcon, StrikeIcon, SunIcon, SystemIcon, WindowIcon } from './icons';
import { MenuItem, Popover, menuKeys } from './Popover';
import { chooseTheme } from './Head';

/**
 * Right-click menus. Each place offers only what belongs there: editing in
 * the text, document actions on a tab, window and view actions on the canvas.
 */
export function ContextMenu() {
  const request = useUi((s) => s.contextMenu);
  const [last, setLast] = useState<ContextMenuRequest | null>(request);
  if (request && request !== last) setLast(request);
  const shown = request ?? last;

  const close = (reason: 'escape' | 'outside' | 'select') => {
    patchUi({ contextMenu: null });
    if (reason !== 'outside') shown?.returnFocus?.focus({ preventScroll: true });
  };

  return (
    <Popover open={!!request} anchor={null} point={shown ? { x: shown.x, y: shown.y } : null} onClose={close} label="Context menu" role="menu" className="context-popover">
      {shown?.target.kind === 'editor' && <EditorMenu windowId={shown.target.windowId} onDone={() => close('select')} />}
      {shown?.target.kind === 'tab' && <TabMenu docId={shown.target.docId} onDone={() => close('select')} />}
      {shown?.target.kind === 'canvas' && <CanvasMenu onDone={() => close('select')} />}
    </Popover>
  );
}

function EditorMenu({ windowId, onDone }: { windowId: string; onDone: () => void }) {
  const app = useApp();
  const view = app.view(windowId);
  const kind = useSession((s) => s.docs.find((d) => d.id === s.windows.find((w) => w.id === windowId)?.activeId)?.kind ?? 'md');
  if (!view) return null;
  const sel = view.state.selection.main;
  const selected = !sel.empty;
  const md = kind === 'md';

  const act = (fn: () => void | Promise<void>) => async () => {
    onDone();
    view.focus();
    await fn();
  };
  const copy = async (cut: boolean) => {
    const text = view.state.sliceDoc(sel.from, sel.to);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      document.execCommand(cut ? 'cut' : 'copy');
      return;
    }
    if (cut) view.dispatch({ changes: { from: sel.from, to: sel.to }, userEvent: 'delete.cut' });
  };
  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      view.dispatch(view.state.replaceSelection(text), { userEvent: 'input.paste', scrollIntoView: true });
    } catch {
      notify(`This browser keeps the clipboard private here. Press ${shortcutLabel('Mod-v')} to paste.`);
    }
  };
  const format = (make: Parameters<typeof runSpec>[1]) => act(() => void runSpec(view, make));

  return (
    <div className="menu" tabIndex={-1} data-autofocus onKeyDown={menuKeys}>
      {md && selected && (
        <>
          <div className="menu-tools" role="group" aria-label="Format">
            <ToolItem label="Bold" icon={<BoldIcon size={16} />} onSelect={format((s) => toggleInline(s, 'bold'))} />
            <ToolItem label="Italic" icon={<ItalicIcon size={16} />} onSelect={format((s) => toggleInline(s, 'italic'))} />
            <ToolItem label="Strikethrough" icon={<StrikeIcon size={16} />} onSelect={format((s) => toggleInline(s, 'strike'))} />
            <ToolItem label="Code" icon={<CodeIcon size={16} />} onSelect={format((s) => toggleInline(s, 'code'))} />
            <ToolItem label="Link" icon={<LinkIcon size={16} />} onSelect={act(() => patchUi({ linkEditor: 'context' }))} />
          </div>
          <div className="menu-sep" role="separator" />
        </>
      )}
      <MenuItem label="Cut" hint={shortcutLabel('Mod-x')} disabled={!selected} onSelect={act(() => copy(true))} />
      <MenuItem label="Copy" hint={shortcutLabel('Mod-c')} disabled={!selected} onSelect={act(() => copy(false))} />
      <MenuItem label="Paste" hint={shortcutLabel('Mod-v')} onSelect={act(paste)} />
      <MenuItem label="Select All" hint={shortcutLabel('Mod-a')} onSelect={act(() => view.dispatch({ selection: { anchor: 0, head: view.state.doc.length } }))} />
      <div className="menu-sep" role="separator" />
      <MenuItem icon={<IndentIcon size={16} />} label="Indent" hint="⇥" onSelect={format((s) => indentLines(s, kind))} />
      <MenuItem icon={<OutdentIcon size={16} />} label="Outdent" hint="⇧⇥" onSelect={format((s) => outdentLines(s, kind))} />
      <p className="menu-footnote">{isMac ? '⌥' : 'Alt'}-click for spelling and the browser menu</p>
    </div>
  );
}

function ToolItem({ label, icon, onSelect }: { label: string; icon: React.ReactNode; onSelect: () => void }) {
  return (
    <button type="button" role="menuitem" className="menu-tool" aria-label={label} data-tip={label} tabIndex={-1} onClick={onSelect}>
      {icon}
    </button>
  );
}

function TabMenu({ docId, onDone }: { docId: string; onDone: () => void }) {
  const app = useApp();
  const doc = useSession((s) => s.docs.find((d) => d.id === docId));
  const owner = useSession((s) => windowOf(s, docId));
  const windows = useSession((s) => s.windows.length);
  if (!doc || !owner) return null;
  const act = (fn: () => unknown) => () => {
    onDone();
    fn();
  };
  return (
    <div className="menu" tabIndex={-1} data-autofocus onKeyDown={menuKeys}>
      {doc.origin !== 'file' && (
        <MenuItem
          label="Rename…"
          onSelect={act(() => {
            app.activate(doc.id, { focus: false });
            requestAnimationFrame(() => openDocumentCard(owner.id));
          })}
        />
      )}
      <MenuItem label="Save" hint={shortcutHint('Mod-s')} disabled={!doc.dirty && doc.origin === 'file'} onSelect={act(() => app.save(doc.id))} />
      <MenuItem label="Save As…" hint={shortcutHint('Mod-Shift-s')} onSelect={act(() => app.saveAs(doc.id))} />
      <div className="menu-sep" role="separator" />
      <MenuItem icon={<WindowIcon size={16} />} label="Move to New Window" disabled={owner.docIds.length < 2 || windows >= MAX_WINDOWS} onSelect={act(() => app.moveToNewWindow(doc.id))} />
      <div className="menu-sep" role="separator" />
      <MenuItem label="Close" hint={shortcutHint('Mod-w')} onSelect={act(() => app.close(doc.id))} />
      <MenuItem label="Close Others" disabled={owner.docIds.length < 2} onSelect={act(() => app.closeOthers(doc.id))} />
    </div>
  );
}

function CanvasMenu({ onDone }: { onDone: () => void }) {
  const app = useApp();
  const windows = useSession((s) => s.windows.length);
  const theme = useUi((s) => s.theme);
  const prefs = useUi((s) => s.prefs);
  const act = (fn: () => unknown) => () => {
    onDone();
    fn();
  };
  return (
    <div className="menu" tabIndex={-1} data-autofocus onKeyDown={menuKeys}>
      <MenuItem icon={<WindowIcon size={16} />} label="New Window" disabled={windows >= MAX_WINDOWS} onSelect={act(() => app.newWindow())} />
      <MenuItem icon={<GridIcon size={16} />} label="Arrange Windows" disabled={windows < 2} onSelect={act(() => app.arrangeWindows('auto'))} />
      <MenuItem icon={<SearchIcon size={16} />} label="Commands…" hint={shortcutLabel('Mod-k')} onSelect={act(() => patchUi({ palette: true }))} />
      {app.cloud.configured && <MenuItem icon={<CloudIcon size={16} />} label="Account & Cloud…" onSelect={act(() => openAccount())} />}
      <div className="menu-sep" role="separator" />
      <MenuItem icon={<SystemIcon size={16} />} label="Match System" checked={theme === 'system'} onSelect={() => chooseTheme('system')} />
      <MenuItem icon={<SunIcon size={16} />} label="Light" checked={theme === 'light'} onSelect={() => chooseTheme('light')} />
      <MenuItem icon={<MoonIcon size={16} />} label="Dark" checked={theme === 'dark'} onSelect={() => chooseTheme('dark')} />
      <div className="menu-sep" role="separator" />
      <MenuItem icon={<LineNumbersIcon size={16} />} label="Line Numbers" toggle checked={prefs.lineNumbers} onSelect={() => app.setPrefs({ lineNumbers: !prefs.lineNumbers })} />
      <MenuItem icon={<CurrentLineIcon size={16} />} label="Highlight Current Line" toggle checked={prefs.currentLine} onSelect={() => app.setPrefs({ currentLine: !prefs.currentLine })} />
    </div>
  );
}
